'use client';

import { skipToken } from '@reduxjs/toolkit/query/react';
import React from 'react';
import { hasHeimdallPermission } from '@highlight-cp/auth-client/access';
import type { HeimdallAuthClient } from '@highlight-cp/auth-client/heimdall-client';
import { createHeimdallAuthClient } from '@highlight-cp/auth-client/heimdall-client';
import { useProjectPublicId } from '@highlight-cp/hooks';
import { createLoggers } from '@highlight-cp/logger';
import { useAppDispatch } from 'app/hooks/redux';

import type { ClientPortalSessionAnswer } from './clientPortalSessionApi';
import { clientPortalSessionApi, useGetClientPortalSessionQuery } from './clientPortalSessionApi';
import { useSessionRecheck } from './sessionRecheck';

export type ClientPortalAuthState = { status: 'loading' } | ClientPortalSessionAnswer;

const ClientPortalAuthContext = React.createContext<ClientPortalAuthState | null>(null);

const ClientPortalAuthClientContext = React.createContext<HeimdallAuthClient | null>(null);

interface ClientPortalAuthClientProviderProps {
  heimdallBaseUrl: string;
  children: React.ReactNode;
}

interface ClientPortalAuthProviderProps {
  children: React.ReactNode;
}

export function ClientPortalAuthClientProvider({
  heimdallBaseUrl,
  children,
}: ClientPortalAuthClientProviderProps) {
  const client = React.useMemo(() => createHeimdallAuthClient(heimdallBaseUrl), [heimdallBaseUrl]);

  return (
    <ClientPortalAuthClientContext.Provider value={client}>
      {children}
    </ClientPortalAuthClientContext.Provider>
  );
}

export function useClientPortalAuthClient() {
  const client = React.useContext(ClientPortalAuthClientContext);

  if (!client) {
    throw new Error('Client Portal auth client provider is missing');
  }

  return client;
}

export function useClientPortalAuth(): ClientPortalAuthState {
  const state = React.useContext(ClientPortalAuthContext);

  if (!state) {
    throw new Error('ClientPortalAuthProvider is missing');
  }

  return state;
}

// For components that also render on static error pages, which have no provider: null there.
export function useOptionalClientPortalAuth(): ClientPortalAuthState | null {
  return React.useContext(ClientPortalAuthContext);
}

const loading: ClientPortalAuthState = { status: 'loading' };
const unauthenticated: ClientPortalAuthState = { status: 'unauthenticated' };

const { info, warn } = createLoggers({ tag: 'Client Portal session' });

export function ClientPortalAuthProvider({ children }: ClientPortalAuthProviderProps) {
  const dispatch = useAppDispatch();
  const projectPublicId = useProjectPublicId() ?? undefined;
  const heimdallSession = useClientPortalAuthClient().useSession();
  const user = heimdallSession.data?.user;
  const sessionArg = user
    ? {
        sessionId: heimdallSession.data?.session?.id ?? null,
        userId: user.id ?? null,
        email: user.email ?? null,
        role: user.role ?? null,
        // Only staff sessions depend on the page's project; a client's answer
        // is the same on every project.
        projectPublicId: hasHeimdallPermission(user.role, { clientPortal: ['staff'] })
          ? projectPublicId
          : undefined,
      }
    : skipToken;
  const sessionQuery = useGetClientPortalSessionQuery(sessionArg);

  useSessionRecheck(async ({ endpointName }) => {
    // The recheck holds its own subscription: a Better Auth 401 skips the
    // hook's query, and with keepUnusedDataFor: 0 that drops the hook's refetch.
    const sessionCall =
      sessionArg === skipToken
        ? undefined
        : dispatch(
            clientPortalSessionApi.endpoints.getClientPortalSession.initiate(sessionArg, {
              forceRefetch: true,
            }),
          );
    // Both calls start together; neither waits on the other. Without a
    // Better Auth user there is no session to ask about.
    const [, rechecked] = await Promise.all([heimdallSession.refetch(), sessionCall]).finally(() =>
      sessionCall?.unsubscribe(),
    );
    const answer = rechecked?.isSuccess ? rechecked.data : undefined;
    // A failed session call keeps the known session; the server logs its own 502s.
    if (!answer) {
      return;
    }
    if (answer.status === 'authenticated') {
      info('Session recheck kept the session', { endpointName });
      return;
    }
    warn('Session recheck ended the session', { outcome: answer.status, endpointName });
  });

  const state = toAuthState({
    isPending: heimdallSession.isPending,
    hasUser: Boolean(user),
    answer: sessionQuery.currentData,
    failed: sessionQuery.isError,
  });

  return (
    <ClientPortalAuthContext.Provider value={state}>{children}</ClientPortalAuthContext.Provider>
  );
}

function toAuthState({
  isPending,
  hasUser,
  answer,
  failed,
}: {
  isPending: boolean;
  hasUser: boolean;
  answer: ClientPortalAuthState | undefined;
  failed: boolean;
}): ClientPortalAuthState {
  if (isPending) {
    return loading;
  }
  if (!hasUser) {
    return unauthenticated;
  }
  // A known answer stays through a failed refetch: RTK keeps it.
  if (answer) {
    return answer;
  }
  // A first-load failure has no known session to keep (F9 decision).
  if (failed) {
    return unauthenticated;
  }

  return loading;
}
